import { Loader2Icon } from "lucide-react"
import { useLayoutEffect, useRef } from "react"
import { Button } from "@/primitives/button.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"

/*
 * The bar a settings form raises while it holds changes nobody has saved: it floats at the foot of
 * the page as the Member scrolls, says what changed, and offers Discard and Save. A form with no
 * changes shows none, so the page renders it only while dirty.
 *
 * ⌘↵ saves and Esc discards while focus is in the form the bar belongs to: the `<form>` it sits in,
 * or else the element it sits in. Discarding takes the bar away, so a Discard pressed in the bar
 * hands the focus back to the form's first control rather than dropping it to the page.
 */

const focusable =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/** The first control of `form` that takes the focus, outside the bar. */
const firstControlOf = (form: Element | null, bar: Element | null) =>
  [...(form?.querySelectorAll<HTMLElement>(focusable) ?? [])].find(
    (control) => !bar?.contains(control),
  )

export function SaveBar({
  summary,
  onDiscard,
  onSave,
  saving,
  invalidReason,
}: {
  /** What changed, such as "Name and description". */
  summary: string
  onDiscard: () => void
  onSave: () => void
  saving: boolean
  /** Why the changes can't be saved as they stand; Save is disabled and says this. */
  invalidReason?: string
}) {
  const bar = useRef<HTMLDivElement>(null)
  const form = useRef<Element | null>(null)
  useLayoutEffect(() => {
    form.current = bar.current?.closest("form") ?? bar.current?.parentElement ?? null
  })

  const canSave = !saving && invalidReason === undefined
  const discard = () => {
    const fromBar = bar.current?.contains(document.activeElement) ?? false
    onDiscard()
    if (fromBar) firstControlOf(form.current, bar.current)?.focus()
  }
  useShortcuts(
    {
      submit: () => {
        if (canSave) onSave()
      },
      discard: () => {
        if (!saving) discard()
      },
    },
    { within: form },
  )

  const save = (
    // Saving, it keeps its colour and the focus, and takes no second press.
    <Button size="sm" disabled={!canSave} focusableWhenDisabled={saving} onClick={onSave}>
      {saving && <Loader2Icon aria-hidden className="animate-spin" />}
      Save
      {!saving && <Kbd shortcut="submit" />}
    </Button>
  )

  return (
    <div
      ref={bar}
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-4 z-20 mx-auto mt-6 flex h-11 w-fit animate-in items-center gap-3 rounded-lg bg-popover px-3 shadow-dialog duration-150 ease-snappy fade-in slide-in-from-bottom-2"
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-primary" />
      <p className="max-w-md truncate text-ui">
        <span className="font-medium">Unsaved</span>
        <span className="text-muted-foreground"> · {summary}</span>
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button variant="ghost" size="sm" disabled={saving} onClick={discard}>
          Discard
        </Button>
        {invalidReason === undefined ? (
          save
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  tabIndex={0}
                  className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              }
            >
              {save}
            </TooltipTrigger>
            <TooltipContent>{invalidReason}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
